// GuildDirectory.tsx
// The "all guilds" page: find a raid team. Styled like the Alignment sign up
// form (alignmentUi.tsx) so the two public-facing pages read as one product:
// Inter, glass panels, a stats sidebar, coloured coverage bars, real spec art.
//
// Every card answers what a recruit actually asks, in this order:
//   what kind of guild is it -> when do they raid (in MY time) -> what are
//   they raiding -> how many show up -> are they looking for me.
// Those come from GET /guilds/directory/stats, measured from the guild's own
// raid history (auth/lib/guild_stats.js), per game. A guild with too little
// history here (new to Rallify, however old in game) falls back to what its
// officers wrote, labelled as stated rather than measured.
//
// Game-aware throughout: stats, needs and realm are per game, and the game
// tabs switch which game every card and the sidebar describe. Faction is
// optional (FFXIV has none), so nothing assumes it.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { DateTime } from "luxon";
import { BODY, DISPLAY, Glass, Icon, ROLE_COLOR, tc, useAlignmentTheme } from "./alignmentUi";
import { useTimezone } from "./lib/TimezoneContext";
import { shortGameName } from "./lib/gameName";

// --- Types (mirror GET /guilds/directory/stats) ---

export type ScheduleSlot = {
  weekday: number; time: string; timezone: string; raids: number;
  durationMinutes: number | null; sampleStartsAt: string;
};
export type ContentEntry = { key: string; label: string; image: string | null; size: number | null; raids: number; lastAt: string };
export type Need = {
  specId: number; priority: "high" | "normal"; label: string; image: string | null;
  roleKey: string | null; classKey: string | null; className: string | null; classColor: string | null;
};
export type GuildStyle = { id: number; key: string; label: string; color: string | null; description: string | null };
export type GuildGameStats = {
  gameId: string; gameName: string;
  serverName: string | null; faction: string | null; region: string | null;
  history: { raidsTotal: number; firstRaidAt: string | null; lastRaidAt: string | null; raidingWeeks: number; windowWeeks: number; verified: boolean };
  raidsPerWeek: number; nextRaidAt: string | null;
  schedule: ScheduleSlot[]; content: ContentEntry[];
  members: { players: number; characters: number };
  roster: { activeRaiders: number; activeCharacters: number; roleMix: { tank: number; healer: number; dps: number }; avgSeated: number; benchRate: number };
  suggestedRoles: Array<{ role: string; have: number; want: number; raid: string }>;
  needs: Need[];
  styles: GuildStyle[];
  logsUrl: string | null;
  logsSite: { provider: string; name: string; origin: string } | null;
};
export type DirectoryStats = Record<string, { games: GuildGameStats[] }>;

/** The viewer's characters, as GET /characters returns them (fields used here). */
export type ViewerCharacter = {
  id: string; name: string; gameId?: string; isSleeping?: boolean;
  options?: { group_id: number; option_id: number }[];
  metadata?: { realm_name?: string | null; class_name?: string | null } | null;
};
type SpecOption = { id: number; label: string; image_url?: string | null; metadata?: any };
/** One of the viewer's specs, on the character (so realm + game) that plays it. */
export type MySpec = { key: string; specId: number; label: string; image: string | null; charName: string; gameId: string; realm: string };

const sameRealm = (a: string | null | undefined, b: string | null | undefined) =>
  !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * Does this guild want one of the picked specs? Only in the character's game
 * and on its realm (you cannot join a guild on another server), and only while
 * recruiting. A guild recruiting with no specs listed is "open to every role",
 * so it wants everyone.
 */
export function guildWantsSpec(g: DirectoryGuild, games: GuildGameStats[], picked: MySpec[]): "listed" | "open" | null {
  return specMatch(g, games, picked)?.kind || null;
}

/** The game in which the guild wants one of the picked specs (listed beats open). */
export function specMatchGame(g: DirectoryGuild, games: GuildGameStats[], picked: MySpec[]): string | null {
  return specMatch(g, games, picked)?.gameId || null;
}

function specMatch(g: DirectoryGuild, games: GuildGameStats[], picked: MySpec[]): { kind: "listed" | "open"; gameId: string } | null {
  if (!g.recruiting || !picked.length) return null;
  let open: string | null = null;
  for (const sp of picked) {
    const game = games.find((x) => x.gameId === sp.gameId);
    if (!game || !sameRealm(game.serverName, sp.realm)) continue;
    if (game.needs.some((n) => n.specId === sp.specId)) return { kind: "listed", gameId: game.gameId };
    if (!game.needs.length && !open) open = game.gameId;
  }
  return open ? { kind: "open", gameId: open } : null;
}

/** Local weekdays (Mon=1) a guild raids on, in the viewer's timezone. */
export function raidNightsInTz(game: GuildGameStats | null, tz: string): Set<number> {
  return new Set((game?.schedule || []).map((sl) => DateTime.fromISO(sl.sampleStartsAt).setZone(tz).weekday));
}

export type DirectoryGuild = {
  id: string; gameId: string; name: string; tag: string;
  serverName?: string | null; faction?: string | null; region?: string | null;
  scheduleSummary?: string | null; logoUrl?: string | null;
  isOwner?: boolean; isOfficer?: boolean; recruiting?: boolean; memberCount?: number;
};

const AUTH_BASE_URL = process.env.REACT_APP_AUTH_BASE_URL || "";
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const ROLE_BAR = { tank: ROLE_COLOR.tank, healer: ROLE_COLOR.healer, dps: ROLE_COLOR.melee_dps };

/** Loads directory stats once; cards render first and fill in behind. */
export function useDirectoryStats(): DirectoryStats | null {
  const [stats, setStats] = useState<DirectoryStats | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetch("/guilds/directory/stats")
      .then((r) => (r.ok ? r.json() : {}))
      .then((d) => { if (!cancelled) setStats(d || {}); })
      .catch(() => { if (!cancelled) setStats({}); });
    return () => { cancelled = true; };
  }, []);
  return stats;
}

function factionColor(faction: string | null | undefined): string | null {
  const f = (faction || "").toLowerCase();
  if (f.includes("alliance")) return "#5AA8FF";
  if (f.includes("horde")) return "#FF5A4E";
  return null; // no faction (or a game without factions): no colour claim
}

function logoSrc(url: string | null | undefined): string | null {
  if (!url) return null;
  return url.startsWith("http") ? url : `${AUTH_BASE_URL}${url}`;
}

/** Which game a card describes: the selected tab, else the guild's most active game. */
function pickGame(games: GuildGameStats[], gameFilter: string): GuildGameStats | null {
  if (!games.length) return null;
  if (gameFilter !== "all") return games.find((g) => g.gameId === gameFilter) || null;
  return [...games].sort((a, b) =>
    (b.history.lastRaidAt || "").localeCompare(a.history.lastRaidAt || "") || b.members.players - a.members.players
  )[0];
}

/**
 * "Tue · Wed 20:00" in the VIEWER's timezone. Each slot's latest real start is
 * converted, so a Berlin guild's 20:00 reads as 14:00 to a New York viewer and
 * the weekday moves with it when the offset crosses midnight.
 */
export function formatSchedule(slots: ScheduleSlot[], tz: string): { text: string; hours: number | null } {
  if (!slots.length) return { text: "", hours: null };
  const local = slots.map((s) => {
    const d = DateTime.fromISO(s.sampleStartsAt).setZone(tz);
    return { day: d.weekday, time: d.toFormat("HH:mm"), minutes: s.durationMinutes };
  }).sort((a, b) => a.day - b.day || a.time.localeCompare(b.time));
  const times = Array.from(new Set(local.map((l) => l.time)));
  const text = times.length === 1
    ? `${local.map((l) => WEEKDAYS[l.day - 1]).join(" · ")} ${times[0]}`
    : local.map((l) => `${WEEKDAYS[l.day - 1]} ${l.time}`).join(" · ");
  const mins = local.map((l) => l.minutes).filter((m): m is number => !!m);
  return { text, hours: mins.length ? Math.round((Math.max(...mins) / 60) * 10) / 10 : null };
}

// --- Small pieces ---

function Eyebrow({ children }: { children: React.ReactNode }) {
  return <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[var(--c-ink-3)]">{children}</div>;
}

function Segmented<T extends string>({
  value, options, onChange,
}: { value: T; options: Array<{ value: T; label: string }>; onChange: (v: T) => void }) {
  return (
    <div className="inline-flex rounded-xl border border-[var(--c-line)] bg-[var(--c-sunken)] p-1">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={`rounded-lg px-3.5 py-1.5 text-[13px] font-semibold transition ${
            value === o.value
              ? "bg-[var(--c-surface)] text-[var(--c-ink)] shadow-[var(--c-shadow)]"
              : "text-[var(--c-ink-3)] hover:text-[var(--c-ink)]"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Tank / healer / DPS split as one segmented bar, like the alignment coverage bars. */
export function RoleMixBar({ mix }: { mix: { tank: number; healer: number; dps: number } }) {
  const total = mix.tank + mix.healer + mix.dps;
  if (!total) return null;
  return (
    <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-[var(--c-sunken-2)]">
      {(["tank", "healer", "dps"] as const).map((r) =>
        mix[r] ? <div key={r} style={{ width: `${(mix[r] / total) * 100}%`, background: ROLE_BAR[r] }} /> : null
      )}
    </div>
  );
}

function Row({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-3">
      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[var(--c-sunken)] text-[var(--c-ink-3)]" aria-hidden>
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-[var(--c-ink-4)]">{label}</div>
        <div className="mt-0.5">{children}</div>
      </div>
    </div>
  );
}

const svg = (d: string) => (
  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d={d} />
  </svg>
);
const ICON_CAL = svg("M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5");
const ICON_SWORD = svg("M14.5 17.5L3 6V3h3l11.5 11.5M13 19l6-6M16 16l4 4M19 21l2-2");
const ICON_USERS = svg("M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z");
const ICON_SEARCH = svg("M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z");

/** A guild style tag (Casual, PvP, ...), coloured from its game config. */
export function StylePill({ style, active, onClick }: { style: GuildStyle; active?: boolean; onClick?: () => void }) {
  const c = style.color || "#94a3b8";
  const base = "inline-flex items-center rounded-full px-2.5 py-0.5 text-[12px] font-semibold transition";
  if (!onClick) {
    return <span title={style.description || undefined} className={base} style={{ color: tc(c), background: `${c}1f` }}>{style.label}</span>;
  }
  return (
    <button
      type="button"
      onClick={onClick}
      title={style.description || undefined}
      aria-pressed={!!active}
      className={`${base} border ${active ? "" : "border-[var(--c-line)] text-[var(--c-ink-3)] hover:text-[var(--c-ink)]"}`}
      style={active ? { color: tc(c), background: `${c}24`, borderColor: `${c}80` } : undefined}
    >
      {style.label}
    </button>
  );
}

// --- Card ---

function DirectoryCard({
  g, stats, statsLoaded, gameFilter, isYours, isAuthed, tz, onOpen, onRequest, onManage,
}: {
  g: DirectoryGuild; stats: GuildGameStats[]; statsLoaded: boolean; gameFilter: string;
  isYours: boolean; isAuthed: boolean; tz: string;
  onOpen: () => void; onRequest: () => void; onManage: () => void;
}) {
  const game = pickGame(stats, gameFilter);
  const otherGames = stats.filter((s) => s !== game);
  const realm = game?.serverName ?? g.serverName;
  const region = game?.region ?? g.region;
  const faction = game ? game.faction : g.faction;
  const fc = factionColor(faction);
  const logo = logoSrc(g.logoUrl);

  const measured = game && game.schedule.length ? formatSchedule(game.schedule, tz) : null;
  const stated = (g.scheduleSummary || "").trim();
  // Only when raids actually carry rosters here: a guild that schedules on
  // Rallify but seats people elsewhere would otherwise read "1 raiding of 21".
  const active = game && game.roster.avgSeated >= 5 ? game.roster.activeRaiders : 0;
  const players = game?.members.players || g.memberCount || 0;
  const needs = game?.needs || [];

  let action: React.ReactNode;
  if (g.isOwner || g.isOfficer) {
    action = <button onClick={(e) => { e.stopPropagation(); onManage(); }} className="w-full rounded-xl border border-[var(--c-line-strong)] bg-[var(--c-sunken)] py-2.5 text-[13px] font-semibold text-[var(--c-ink)] transition hover:bg-[var(--c-sunken-2)]">Manage guild</button>;
  } else if (g.recruiting && isAuthed) {
    action = <button onClick={(e) => { e.stopPropagation(); onRequest(); }} className="w-full rounded-xl bg-[var(--c-accent)] py-2.5 text-[13px] font-bold text-[var(--c-ink-on-fill)] transition hover:bg-[var(--c-accent-hover)] dark:bg-gradient-to-b dark:from-amber-300 dark:to-amber-500">Request to join</button>;
  } else {
    action = <button onClick={(e) => { e.stopPropagation(); onOpen(); }} className="w-full rounded-xl border border-[var(--c-line)] py-2.5 text-[13px] font-semibold text-[var(--c-ink-2)] transition hover:bg-[var(--c-sunken)]">View guild</button>;
  }

  return (
    <div
      onClick={onOpen}
      className="group flex cursor-pointer flex-col overflow-hidden rounded-3xl border border-[var(--c-line)] bg-[color-mix(in_srgb,var(--c-surface)_85%,transparent)] shadow-[var(--c-shadow-lg)] backdrop-blur-xl transition hover:-translate-y-0.5 hover:border-[var(--c-line-strong)]"
    >
      {/* Banner: the guild's own art, kept dark whatever the palette. */}
      <div className="lfm-dark relative h-28 overflow-hidden bg-black">
        {logo ? (
          <img src={logo} alt="" className="h-full w-full object-cover opacity-80 transition-transform duration-500 group-hover:scale-105" />
        ) : (
          <div className="char-modal-splash-idle h-full w-full" />
        )}
        <div className="absolute inset-0" style={{ background: `linear-gradient(to top, rgba(0,0,0,0.75), transparent 70%)${fc ? `, radial-gradient(120% 100% at 100% 0%, ${fc}40, transparent 60%)` : ""}` }} />
        <div className="absolute left-3 top-3 flex gap-1.5">
          {g.isOwner && <span className="rounded-full bg-black/50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-cyan-200 backdrop-blur">Owner</span>}
          {isYours && !g.isOwner && <span className="rounded-full bg-black/50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-200 backdrop-blur">Member</span>}
        </div>
        {g.recruiting && (
          <span className="absolute right-3 top-3 flex items-center gap-1.5 rounded-full bg-emerald-500/90 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white">
            <span className="h-1.5 w-1.5 rounded-full bg-white" /> Recruiting
          </span>
        )}
        <div className="absolute bottom-2.5 left-3 flex flex-wrap gap-1.5">
          {game && <span className="rounded-md bg-white/15 px-2 py-0.5 text-[10px] font-semibold text-white backdrop-blur">{shortGameName(game.gameName)}</span>}
          {gameFilter === "all" && otherGames.map((o) => (
            <span key={o.gameId} className="rounded-md bg-black/40 px-2 py-0.5 text-[10px] font-semibold text-white/70 backdrop-blur">+ {shortGameName(o.gameName)}</span>
          ))}
        </div>
      </div>

      <div className="flex flex-1 flex-col gap-4 p-5">
        <div>
          <div className="flex items-baseline gap-2">
            <h3 className="truncate text-xl text-[var(--c-ink)]" style={DISPLAY}>{g.name}</h3>
            {g.tag && <span className="shrink-0 font-mono text-xs text-[var(--c-ink-4)]">[{g.tag}]</span>}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 text-[13px] text-[var(--c-ink-3)]">
            {region && <span className="font-semibold">{region}</span>}
            {realm && <span>· {realm}</span>}
            {fc && faction && (
              <span className="flex items-center gap-1.5">· <span className="h-2 w-2 rounded-full" style={{ background: fc }} /><span style={{ color: tc(fc) }}>{faction}</span></span>
            )}
          </div>
          {game && game.styles.length > 0 && (
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {game.styles.map((st) => <StylePill key={st.id} style={st} />)}
            </div>
          )}
        </div>

        <Row icon={ICON_CAL} label={measured ? "Raids" : "Schedule"}>
          {!statsLoaded ? (
            <div className="h-4 w-32 animate-pulse rounded bg-[var(--c-sunken-2)]" />
          ) : measured ? (
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="text-[15px] font-semibold text-[var(--c-ink)]">{measured.text}</span>
              {measured.hours && <span className="text-[13px] text-[var(--c-ink-3)]">{measured.hours}h</span>}
              {game!.history.verified && (
                <span title={`Measured from ${game!.history.raidingWeeks} raid weeks of the last ${game!.history.windowWeeks}, shown in your time`} className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-400">✓ measured</span>
              )}
            </div>
          ) : stated ? (
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="text-[14px] text-[var(--c-ink-2)]">{stated}</span>
              <span className="text-[11px] font-semibold text-[var(--c-ink-4)]">as stated</span>
            </div>
          ) : (
            <span className="text-[14px] text-[var(--c-ink-4)]">No raids on Rallify yet</span>
          )}
        </Row>

        {game && game.content.length > 0 && (
          <Row icon={ICON_SWORD} label="Raiding">
            <div className="flex flex-wrap gap-1.5">
              {game.content.slice(0, 3).map((c) => (
                <span key={c.key} className="flex items-center gap-1.5 rounded-lg border border-[var(--c-line)] bg-[var(--c-sunken)] py-0.5 pl-0.5 pr-2 text-[12px] font-semibold text-[var(--c-ink-2)]">
                  <Icon src={c.image} alt={c.label} size={20} />
                  {c.label}{c.size ? <span className="font-normal text-[var(--c-ink-4)]">{c.size}</span> : null}
                </span>
              ))}
            </div>
          </Row>
        )}

        {(active > 0 || players > 0) && (
          <Row icon={ICON_USERS} label="Roster">
            <div className="flex items-baseline gap-2 text-[14px]">
              {active > 0 ? (
                <>
                  <span className="font-semibold text-[var(--c-ink)]">{active} raiding</span>
                  <span className="text-[var(--c-ink-3)]">of {players} players</span>
                </>
              ) : (
                <span className="font-semibold text-[var(--c-ink)]">{players} players</span>
              )}
            </div>
            {active > 0 && <div className="mt-1.5"><RoleMixBar mix={game!.roster.roleMix} /></div>}
          </Row>
        )}

        <div className="mt-auto rounded-2xl bg-[var(--c-sunken)] px-3.5 py-3">
          {!g.recruiting ? (
            <div className="text-[13px] text-[var(--c-ink-4)]">Not recruiting right now</div>
          ) : needs.length ? (
            <>
              <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.14em] text-[var(--c-ink-3)]">Looking for</div>
              <div className="flex flex-wrap gap-2">
                {needs.map((n) => (
                  <span key={n.specId} className="relative" title={`${n.label} ${n.className || ""}${n.priority === "high" ? " · high priority" : ""}`}>
                    <Icon src={n.image} alt={n.label} size={30} ring={n.priority === "high" ? "#f59e0b" : null} />
                  </span>
                ))}
              </div>
            </>
          ) : (
            <div className="text-[13px] font-semibold text-[var(--c-ink-2)]">Recruiting · open to every role</div>
          )}
        </div>

        {action}
      </div>
    </div>
  );
}

// --- Sidebar ---

function Bar({ label, value, max, color, active, onClick }: {
  label: string; value: number; max: number; color?: string; active?: boolean; onClick?: () => void;
}) {
  const inner = (
    <>
      <span className={`w-10 text-left text-[12px] font-semibold ${active ? "text-[var(--c-accent-ink)]" : "text-[var(--c-ink-2)]"}`}>{label}</span>
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[var(--c-sunken-2)]">
        <div className="h-full rounded-full" style={{ width: `${max ? (value / max) * 100 : 0}%`, background: color || "var(--c-accent)" }} />
      </div>
      <span className="w-5 text-right text-[12px] font-bold text-[var(--c-ink)]">{value}</span>
    </>
  );
  if (!onClick) return <div className="flex items-center gap-3">{inner}</div>;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={!!active}
      className={`-mx-2 flex w-[calc(100%+1rem)] items-center gap-3 rounded-lg px-2 py-1 transition ${active ? "bg-amber-500/15 ring-1 ring-[var(--c-accent)]" : "hover:bg-[var(--c-sunken)]"}`}
    >
      {inner}
    </button>
  );
}

function DirectorySidebar({
  guilds, stats, gameFilter, tz, nightFilter, onToggleNight, onClearNights, specPanel,
}: {
  guilds: DirectoryGuild[]; stats: DirectoryStats | null; gameFilter: string; tz: string;
  nightFilter: number[]; onToggleNight: (day: number) => void; onClearNights: () => void;
  specPanel?: React.ReactNode;
}) {
  const summary = useMemo(() => {
    const nights = [0, 0, 0, 0, 0, 0, 0];
    const wanted = new Map<number, { need: Need; guilds: number }>();
    const raiding = new Map<string, { entry: ContentEntry; guilds: number }>();
    let recruiting = 0, measured = 0;
    for (const g of guilds) {
      if (g.recruiting) recruiting++;
      const game = pickGame(stats?.[g.id]?.games || [], gameFilter);
      if (!game) continue;
      if (game.history.verified) measured++;
      raidNightsInTz(game, tz).forEach((d) => { nights[d - 1]++; });
      if (g.recruiting) for (const n of game.needs) {
        const e = wanted.get(n.specId) || { need: n, guilds: 0 };
        e.guilds++;
        wanted.set(n.specId, e);
      }
      for (const c of game.content.slice(0, 3)) {
        const e = raiding.get(c.key) || { entry: c, guilds: 0 };
        e.guilds++;
        raiding.set(c.key, e);
      }
    }
    return {
      nights, recruiting, measured,
      wanted: Array.from(wanted.values()).sort((a, b) => b.guilds - a.guilds).slice(0, 10),
      raiding: Array.from(raiding.values()).sort((a, b) => b.guilds - a.guilds).slice(0, 5),
    };
  }, [guilds, stats, gameFilter, tz]);
  const maxNight = Math.max(1, ...summary.nights);

  return (
    <div className="space-y-4 lg:sticky lg:top-6">
      <Glass className="p-5">
        <Eyebrow>The directory so far</Eyebrow>
        <div className="mt-2 flex items-baseline gap-2">
          <span className="text-4xl text-[var(--c-accent-ink)]" style={DISPLAY}>{guilds.length}</span>
          <span className="text-[14px] text-[var(--c-ink-2)]">guilds</span>
        </div>
        <div className="mt-1 text-[13px] text-[var(--c-ink-3)]">
          <span className="font-semibold text-emerald-700 dark:text-emerald-400">{summary.recruiting} recruiting</span>
          {" · "}{summary.measured} with a measured schedule
        </div>
      </Glass>

      <Glass className="p-5">
        <div className="flex items-baseline justify-between">
          <Eyebrow>Raid nights</Eyebrow>
          {nightFilter.length > 0 ? (
            <button onClick={onClearNights} className="text-[11px] font-semibold text-[var(--c-accent-ink)] hover:underline">Clear</button>
          ) : (
            <span className="text-[11px] text-[var(--c-ink-4)]">in your time · tap to filter</span>
          )}
        </div>
        <div className="mt-3 space-y-1">
          {WEEKDAYS.map((d, i) => (
            <Bar
              key={d}
              label={d}
              value={summary.nights[i]}
              max={maxNight}
              active={nightFilter.includes(i + 1)}
              onClick={() => onToggleNight(i + 1)}
            />
          ))}
        </div>
      </Glass>

      {specPanel}

      {summary.wanted.length > 0 && (
        <Glass className="p-5">
          <Eyebrow>Most wanted</Eyebrow>
          <div className="mt-3 flex flex-wrap gap-2.5">
            {summary.wanted.map(({ need, guilds: n }) => (
              <span key={need.specId} className="relative" title={`${need.label} ${need.className || ""}: ${n} guild${n === 1 ? "" : "s"}`}>
                <Icon src={need.image} alt={need.label} size={34} ring={need.classColor ? tc(need.classColor) : null} />
                {n > 1 && (
                  <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--c-accent)] px-1 text-[10px] font-bold text-[var(--c-ink-on-fill)]">{n}</span>
                )}
              </span>
            ))}
          </div>
        </Glass>
      )}

      {summary.raiding.length > 0 && (
        <Glass className="p-5">
          <Eyebrow>Being raided</Eyebrow>
          <div className="mt-3 space-y-2">
            {summary.raiding.map(({ entry, guilds: n }) => (
              <div key={entry.key} className="flex items-center gap-2.5">
                <Icon src={entry.image} alt={entry.label} size={26} />
                <span className="flex-1 truncate text-[13px] font-semibold text-[var(--c-ink-2)]">{entry.label}</span>
                <span className="text-[12px] text-[var(--c-ink-3)]">{n} guild{n === 1 ? "" : "s"}</span>
              </div>
            ))}
          </div>
        </Glass>
      )}
    </div>
  );
}

// --- Remembered view state ---
//
// The directory unmounts whenever the player opens a guild or switches tab, so
// its filters live in browser storage and come back as they were. Filters use
// localStorage (they survive closing the browser); the search box and scroll
// position use sessionStorage, so an old search doesn't quietly hide guilds
// days later. Storage can be unavailable (private mode), so every access is
// guarded and the page works without it.

const STORE_PREFIX = "lfm.guildDirectory.";

function readStored<T>(storage: "local" | "session", key: string, fallback: T, valid: (v: unknown) => v is T): T {
  try {
    const raw = (storage === "local" ? localStorage : sessionStorage).getItem(STORE_PREFIX + key);
    if (raw === null) return fallback;
    const parsed = JSON.parse(raw);
    return valid(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
}

function useStoredState<T>(storage: "local" | "session", key: string, fallback: T, valid: (v: unknown) => v is T) {
  const [value, setValue] = useState<T>(() => readStored(storage, key, fallback, valid));
  useEffect(() => {
    try {
      (storage === "local" ? localStorage : sessionStorage).setItem(STORE_PREFIX + key, JSON.stringify(value));
    } catch {
      /* storage unavailable: the filter just won't be remembered */
    }
  }, [storage, key, value]);
  return [value, setValue] as const;
}

const isString = (v: unknown): v is string => typeof v === "string";
const isScope = (v: unknown): v is "all" | "mine" | "recruiting" => v === "all" || v === "mine" || v === "recruiting";
const isStringList = (v: unknown): v is string[] => Array.isArray(v) && v.every((x) => typeof x === "string");
const isNumberList = (v: unknown): v is number[] => Array.isArray(v) && v.every((x) => typeof x === "number");

// --- Page ---

export default function GuildDirectory({
  guilds, yourGuildIds, games, isAuthed, creationLocked, joinPanel, characters = [],
  onOpen, onRequest, onManage, onCreate,
}: {
  characters?: ViewerCharacter[];
  guilds: DirectoryGuild[];
  yourGuildIds: Set<string>;
  games: Array<{ id: string; name: string; guildStyles?: GuildStyle[] }>;
  isAuthed: boolean;
  creationLocked: boolean;
  joinPanel?: React.ReactNode;
  onOpen: (g: DirectoryGuild) => void;
  onRequest: (g: DirectoryGuild) => void;
  onManage: (g: DirectoryGuild) => void;
  onCreate: () => void;
}) {
  useAlignmentTheme();
  const tz = useTimezone();
  const stats = useDirectoryStats();
  const [storedGame, setGameFilter] = useStoredState("local", "game", "all", isString);
  const [scope, setScope] = useStoredState("local", "scope", "all", isScope);
  const [query, setQuery] = useStoredState("session", "query", "", isString);
  const [styleFilter, setStyleFilter] = useStoredState<string[]>("local", "styles", [], isStringList);
  const [nightFilter, setNightFilter] = useStoredState<number[]>("local", "nights", [], isNumberList);
  const [specFilter, setSpecFilter] = useStoredState<string[]>("local", "specs", [], isStringList); // MySpec.key
  // A remembered game that no longer exists falls back to All. While the game
  // list is still loading, keep the remembered one rather than flashing All.
  const gameFilter = storedGame === "all" || games.length === 0 || games.some((gm) => gm.id === storedGame) ? storedGame : "all";

  // Put the list back where the player left it. The page scrolls the window;
  // save as they scroll, restore once the guilds have arrived.
  const restoredScroll = useRef(false);
  useEffect(() => {
    if (restoredScroll.current || guilds.length === 0) return;
    restoredScroll.current = true;
    const y = readStored("session", "scrollY", 0, (v): v is number => typeof v === "number");
    // Wait a tick so the cards have laid out and the page is tall enough.
    if (y > 0) window.setTimeout(() => window.scrollTo({ top: y }), 0);
  }, [guilds.length]);
  useEffect(() => {
    // Debounced save. A pending save is dropped on unmount, so the scroll reset
    // that happens when the player leaves the page can't overwrite the spot.
    let timer = 0;
    const onScroll = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        if (!restoredScroll.current) return; // don't overwrite it before we've restored
        try { sessionStorage.setItem(STORE_PREFIX + "scrollY", JSON.stringify(Math.round(window.scrollY))); } catch { /* ignore */ }
      }, 150);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.clearTimeout(timer); window.removeEventListener("scroll", onScroll); };
  }, []);

  // Spec art for the viewer's games, to show "your specs" as real icons.
  const charGameIds = useMemo(
    () => Array.from(new Set(characters.map((c) => c.gameId).filter((x): x is string => !!x))).sort().join(","),
    [characters]
  );
  const [specCatalog, setSpecCatalog] = useState<Record<string, SpecOption[]>>({});
  useEffect(() => {
    if (!charGameIds) return;
    let cancelled = false;
    Promise.all(charGameIds.split(",").map((id) =>
      fetch(`/games/${encodeURIComponent(id)}/fields`, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : []))
        .then((groups: Array<{ key: string; options: SpecOption[] }>) => [id, groups.find((gr) => gr.key === "spec")?.options || []] as const)
        .catch(() => [id, [] as SpecOption[]] as const)
    )).then((entries) => { if (!cancelled) setSpecCatalog(Object.fromEntries(entries)); });
    return () => { cancelled = true; };
  }, [charGameIds]);

  // Every spec the viewer's awake characters play, per character (the realm
  // matters: a guild on another server cannot take that character).
  const mySpecs = useMemo<MySpec[]>(() => {
    const out: MySpec[] = [];
    for (const c of characters) {
      const realm = c.metadata?.realm_name;
      if (c.isSleeping || !c.gameId || !realm) continue;
      const ids = new Set((c.options || []).map((o) => o.option_id));
      for (const sp of specCatalog[c.gameId] || []) {
        if (!ids.has(sp.id)) continue;
        out.push({
          key: `${c.id}:${sp.id}`, specId: sp.id, label: sp.label, image: sp.image_url || null,
          charName: c.name, gameId: c.gameId, realm,
        });
      }
    }
    return out;
  }, [characters, specCatalog]);
  const pickedSpecs = useMemo(() => mySpecs.filter((m) => specFilter.includes(m.key)), [mySpecs, specFilter]);

  // Style filter options: the selected game's list, or every game's merged by
  // key (a Casual guild is Casual whichever game it plays).
  const styleOptions = useMemo(() => {
    const byKey = new Map<string, GuildStyle>();
    for (const gm of games) {
      if (gameFilter !== "all" && gm.id !== gameFilter) continue;
      for (const st of gm.guildStyles || []) if (!byKey.has(st.key)) byKey.set(st.key, st);
    }
    return Array.from(byKey.values());
  }, [games, gameFilter]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return guilds
      .filter((g) => scope !== "mine" || yourGuildIds.has(g.id))
      .filter((g) => scope !== "recruiting" || g.recruiting)
      .filter((g) => {
        if (gameFilter === "all") return true;
        const s = stats?.[g.id]?.games;
        return s ? s.some((x) => x.gameId === gameFilter) : g.gameId === gameFilter;
      })
      // Raids on any of the picked nights (viewer's timezone).
      .filter((g) => {
        if (!nightFilter.length) return true;
        const nights = raidNightsInTz(pickGame(stats?.[g.id]?.games || [], gameFilter), tz);
        return nightFilter.some((d) => nights.has(d));
      })
      // Recruiting one of the picked specs, on that character's realm.
      .filter((g) => !pickedSpecs.length || guildWantsSpec(g, stats?.[g.id]?.games || [], pickedSpecs) !== null)
      // Any of the picked styles, on the game the card describes.
      .filter((g) => {
        if (!styleFilter.length) return true;
        const game = pickGame(stats?.[g.id]?.games || [], gameFilter);
        return !!game && game.styles.some((st) => styleFilter.includes(st.key));
      })
      .filter((g) => !q || g.name.toLowerCase().includes(q) || (g.tag || "").toLowerCase().includes(q) || (g.serverName || "").toLowerCase().includes(q))
      // Recruiting first, then the most recently active.
      .sort((a, b) => {
        // A guild that LISTS your spec beats one merely open to everyone.
        if (pickedSpecs.length) {
          const ra = guildWantsSpec(a, stats?.[a.id]?.games || [], pickedSpecs) === "listed" ? 1 : 0;
          const rb = guildWantsSpec(b, stats?.[b.id]?.games || [], pickedSpecs) === "listed" ? 1 : 0;
          if (ra !== rb) return rb - ra;
        }
        if (!!b.recruiting !== !!a.recruiting) return b.recruiting ? 1 : -1;
        const la = pickGame(stats?.[a.id]?.games || [], gameFilter)?.history.lastRaidAt || "";
        const lb = pickGame(stats?.[b.id]?.games || [], gameFilter)?.history.lastRaidAt || "";
        return lb.localeCompare(la) || a.name.localeCompare(b.name);
      });
  }, [guilds, yourGuildIds, stats, gameFilter, scope, query, styleFilter, nightFilter, pickedSpecs, tz]);

  const inGame = gameFilter === "all" ? guilds : guilds.filter((g) => (stats?.[g.id]?.games || []).some((x) => x.gameId === gameFilter) || g.gameId === gameFilter);

  return (
    <div style={BODY}>
      <div className="mb-8 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-[var(--c-accent-ink)]">Guilds</div>
          <h1 className="mt-2 text-4xl leading-tight text-[var(--c-ink)] sm:text-5xl" style={DISPLAY}>Find your raid team</h1>
          <p className="mt-2 max-w-xl text-[15px] text-[var(--c-ink-3)]">
            Raid nights are measured from each guild's real raids and shown in your time.
          </p>
        </div>
        {isAuthed && (creationLocked ? (
          <span title="Guild creation is limited during the launch wave. Ask us for access on Discord." className="self-start rounded-xl bg-[var(--c-sunken-2)] px-4 py-2.5 text-[13px] font-semibold text-[var(--c-ink-3)] lg:self-auto">
            Create guild (limited during launch)
          </span>
        ) : (
          <button onClick={onCreate} className="self-start rounded-xl bg-[var(--c-accent)] px-5 py-2.5 text-[14px] font-bold text-[var(--c-ink-on-fill)] shadow-[var(--c-shadow)] transition hover:bg-[var(--c-accent-hover)] dark:bg-gradient-to-b dark:from-amber-300 dark:to-amber-500 lg:self-auto">
            + Create guild
          </button>
        ))}
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        {games.length > 1 && (
          <Segmented
            value={gameFilter}
            onChange={setGameFilter}
            options={[{ value: "all", label: "All games" }, ...games.map((g) => ({ value: g.id, label: shortGameName(g.name) }))]}
          />
        )}
        <Segmented
          value={scope}
          onChange={setScope}
          options={[
            { value: "all", label: "All" },
            { value: "recruiting", label: "Recruiting" },
            ...(isAuthed ? [{ value: "mine" as const, label: "My guilds" }] : []),
          ]}
        />
        <label className="relative ml-auto w-full sm:w-64">
          <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--c-ink-4)]">{ICON_SEARCH}</span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search guild, tag or realm"
            className="w-full rounded-xl border border-[var(--c-line)] bg-[var(--c-surface)] py-2 pl-9 pr-3 text-[14px] text-[var(--c-ink)] outline-none transition focus:border-[var(--c-accent)]"
          />
        </label>
      </div>

      {styleOptions.length > 0 && (
        <div className="-mt-2 mb-6 flex flex-wrap items-center gap-2">
          <span className="mr-1 text-[11px] font-bold uppercase tracking-[0.18em] text-[var(--c-ink-4)]">Style</span>
          {styleOptions.map((st) => (
            <StylePill
              key={st.key}
              style={st}
              active={styleFilter.includes(st.key)}
              onClick={() => setStyleFilter((f) => (f.includes(st.key) ? f.filter((k) => k !== st.key) : [...f, st.key]))}
            />
          ))}
          {styleFilter.length > 0 && (
            <button onClick={() => setStyleFilter([])} className="ml-1 text-[12px] font-semibold text-[var(--c-ink-3)] underline-offset-2 hover:underline">Clear</button>
          )}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div className="min-w-0 space-y-6">
          {joinPanel}
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 2xl:grid-cols-3">
            {visible.map((g) => (
              <DirectoryCard
                key={g.id}
                g={g}
                stats={stats?.[g.id]?.games || []}
                statsLoaded={stats !== null}
                // A spec match happened in one game: show that game on the card.
                gameFilter={gameFilter !== "all" ? gameFilter : specMatchGame(g, stats?.[g.id]?.games || [], pickedSpecs) || "all"}
                isYours={yourGuildIds.has(g.id)}
                isAuthed={isAuthed}
                tz={tz}
                onOpen={() => onOpen(g)}
                onRequest={() => onRequest(g)}
                onManage={() => onManage(g)}
              />
            ))}
            {visible.length === 0 && (
              <div className="col-span-full rounded-3xl border border-dashed border-[var(--c-line)] py-16 text-center text-[14px] text-[var(--c-ink-3)]">
                No guilds match. Try another game, style or search.
              </div>
            )}
          </div>
        </div>
        <DirectorySidebar
          guilds={inGame}
          stats={stats}
          gameFilter={gameFilter}
          tz={tz}
          nightFilter={nightFilter}
          onToggleNight={(d) => setNightFilter((f) => (f.includes(d) ? f.filter((x) => x !== d) : [...f, d]))}
          onClearNights={() => setNightFilter([])}
          specPanel={mySpecs.length > 0 ? (
            <Glass className="p-5">
              <div className="flex items-baseline justify-between">
                <Eyebrow>Recruiting your specs</Eyebrow>
                {specFilter.length > 0 && (
                  <button onClick={() => setSpecFilter([])} className="text-[11px] font-semibold text-[var(--c-accent-ink)] hover:underline">Clear</button>
                )}
              </div>
              <p className="mt-1 text-[12px] text-[var(--c-ink-4)]">Pick specs to see guilds on that character's realm looking for them.</p>
              <div className="mt-3 space-y-3">
                {Array.from(new Set(mySpecs.map((m) => m.key.split(":")[0]))).map((charId) => {
                  const specsOf = mySpecs.filter((m) => m.key.startsWith(`${charId}:`));
                  return (
                    <div key={charId}>
                      <div className="text-[12px] font-semibold text-[var(--c-ink-2)]">
                        {specsOf[0].charName} <span className="font-normal text-[var(--c-ink-4)]">· {specsOf[0].realm}</span>
                      </div>
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {specsOf.map((m) => {
                          const on = specFilter.includes(m.key);
                          return (
                            <button
                              key={m.key}
                              type="button"
                              onClick={() => setSpecFilter((f) => (f.includes(m.key) ? f.filter((x) => x !== m.key) : [...f, m.key]))}
                              aria-pressed={on}
                              title={m.label}
                              className={`flex items-center gap-1.5 rounded-lg border py-0.5 pl-0.5 pr-2 text-[12px] font-semibold transition ${on ? "border-[var(--c-accent)] bg-[var(--c-surface)] text-[var(--c-ink)]" : "border-[var(--c-line)] text-[var(--c-ink-3)] hover:text-[var(--c-ink)]"}`}
                            >
                              <Icon src={m.image} alt={m.label} size={22} />
                              {m.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
              {specFilter.length > 0 && (
                <p className="mt-3 text-[11px] text-[var(--c-ink-4)]">Includes guilds recruiting every role. Ones that list your spec come first.</p>
              )}
            </Glass>
          ) : null}
        />
      </div>
    </div>
  );
}
